import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Rating,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Container,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  SentimentVerySatisfied as SentimentVerySatisfiedIcon,
  SentimentSatisfied as SentimentSatisfiedIcon,
  SentimentDissatisfied as SentimentDissatisfiedIcon
} from '@mui/icons-material';
import axios from 'axios';

// Define the API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://hotels.myabad.com';

// Define feedback submission interface
interface FeedbackSubmission {
  token: string;
  overall_rating: number;
  cleanliness_rating: number;
  service_rating: number;
  food_rating: number;
  value_rating: number;
  comments: string;
  would_recommend: boolean;
}

// Custom Rating component with emojis
const CustomRating: React.FC<{
  name: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
}> = ({ name, label, value, onChange }) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography component="legend" sx={{ mb: 1, fontWeight: 'medium' }}>
        {label}
      </Typography>
      <Rating
        name={name}
        value={value}
        onChange={(_, newValue) => {
          onChange(newValue || 0);
        }}
        max={5}
        size="large"
        icon={<SentimentVerySatisfiedIcon fontSize="inherit" color="primary" />}
        emptyIcon={<SentimentDissatisfiedIcon fontSize="inherit" />}
      />
    </Box>
  );
};

const FeedbackForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<FeedbackSubmission>({
    token: token || '',
    overall_rating: 0,
    cleanliness_rating: 0,
    service_rating: 0,
    food_rating: 0,
    value_rating: 0,
    comments: '',
    would_recommend: false
  });
  
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [reservationInfo, setReservationInfo] = useState<{
    guest_name: string;
    property_name: string;
    already_submitted: boolean;
  } | null>(null);
  
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid feedback token');
        setLoading(false);
        return;
      }
      
      try {
        // Fetch reservation info using the token
        const response = await axios.get(`${API_BASE_URL}/public/validate-feedback-token/${token}/`);
        setReservationInfo(response.data.data);
      } catch (err: any) {
        console.error('Error validating token:', err);
        setError(err.response?.data?.message || 'Invalid or expired feedback token');
      } finally {
        setLoading(false);
      }
    };
    
    validateToken();
  }, [token]);
  
  const handleRatingChange = (field: keyof FeedbackSubmission) => (value: number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, comments: e.target.value }));
  };
  
  const handleRecommendChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, would_recommend: e.target.checked }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate ratings
    if (
      formData.overall_rating === 0 ||
      formData.cleanliness_rating === 0 ||
      formData.service_rating === 0 ||
      formData.food_rating === 0 ||
      formData.value_rating === 0
    ) {
      setError('Please provide a rating for all categories');
      return;
    }
    
    setError(null);
    setSubmitting(true);
    
    try {
      await axios.post(`${API_BASE_URL}/public/submit-feedback/`, formData);
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (err: any) {
      console.error('Error submitting feedback:', err);
      setError(err.response?.data?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ my: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading feedback form...
        </Typography>
      </Container>
    );
  }
  
  if (error && !reservationInfo) {
    return (
      <Container maxWidth="md" sx={{ my: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Typography variant="body1" textAlign="center">
          The feedback link appears to be invalid or has expired.
        </Typography>
      </Container>
    );
  }
  
  if (reservationInfo?.already_submitted || success) {
    return (
      <Container maxWidth="md" sx={{ my: 4 }}>
        <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <SentimentVerySatisfiedIcon color="primary" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Thank You!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your feedback has been successfully submitted. We appreciate you taking the time to share your experience with us.
            </Typography>
            {reservationInfo?.property_name && (
              <Typography variant="body2" color="text.secondary">
                - The {reservationInfo.property_name} Team
              </Typography>
            )}
          </CardContent>
        </Card>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center">
            Share Your Feedback
          </Typography>
          
          {reservationInfo && (
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="subtitle1" gutterBottom>
                Dear {reservationInfo.guest_name},
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Thank you for staying with us at {reservationInfo.property_name}. We value your feedback to help us improve our services.
              </Typography>
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <CustomRating
                  name="overall_rating"
                  label="Overall Experience"
                  value={formData.overall_rating}
                  onChange={handleRatingChange('overall_rating')}
                />
                
                <CustomRating
                  name="cleanliness_rating"
                  label="Cleanliness"
                  value={formData.cleanliness_rating}
                  onChange={handleRatingChange('cleanliness_rating')}
                />
                
                <CustomRating
                  name="service_rating"
                  label="Service & Staff"
                  value={formData.service_rating}
                  onChange={handleRatingChange('service_rating')}
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <CustomRating
                  name="food_rating"
                  label="Food & Beverage"
                  value={formData.food_rating}
                  onChange={handleRatingChange('food_rating')}
                />
                
                <CustomRating
                  name="value_rating"
                  label="Value for Money"
                  value={formData.value_rating}
                  onChange={handleRatingChange('value_rating')}
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.would_recommend}
                      onChange={handleRecommendChange}
                      color="primary"
                    />
                  }
                  label="I would recommend this property to others"
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Additional Comments
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  placeholder="Please share any additional feedback about your stay (optional)"
                  value={formData.comments}
                  onChange={handleCommentChange}
                />
              </Grid>
              
              <Grid size={{ xs: 12 }} sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={submitting}
                  sx={{ px: 4, py: 1 }}
                >
                  {submitting ? 'Submitting...' : 'Submit Feedback'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default FeedbackForm; 